import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createNewsletterPost,
  deleteNewsletterPost,
  fetchNewsletterPosts,
  previewNewsletterLink,
  updateNewsletterPost,
  uploadNewsletterImage,
} from "../../api/newsletterApi";
import { formatIssueRange } from "./newsletterFormat";

const EMPTY = {
  kind: "announcement",
  title: "",
  body: "",
  url: "",
  image_url: "",
  pinned: false,
  publish_from: "",
  expires_at: "",
};

const KINDS = ["announcement", "news", "link", "video"];

function formFromPost(post) {
  return {
    kind: post.kind || "announcement",
    title: post.title || "",
    body: post.body || "",
    url: post.url || "",
    image_url: post.image_url && post.image_url !== post.preview?.image ? post.image_url : "",
    pinned: Boolean(post.pinned),
    publish_from: post.publish_from || "",
    expires_at: post.expires_at || "",
  };
}

export function NewsletterPostComposer() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState({ upcoming: [], past: [] });
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [linkPreview, setLinkPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const kindLabel = (kind) =>
    ({
      announcement: t("newsletterKindAnnouncement", "Announcement"),
      news: t("newsletterKindNews", "News"),
      link: t("newsletterKindLink", "Link"),
      video: t("newsletterKindVideo", "Video"),
    })[kind] || kind;

  const load = useCallback(async () => {
    const data = await fetchNewsletterPosts();
    setPosts({ upcoming: data?.upcoming || [], past: data?.past || [] });
  }, []);

  useEffect(() => {
    load().catch((err) => setError(err.message));
  }, [load]);

  const set = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onUrlBlur = async () => {
    const url = form.url.trim();
    if (!url) {
      setLinkPreview(null);
      return;
    }
    try {
      const data = await previewNewsletterLink(url);
      setLinkPreview(data?.preview || null);
      if (data?.video && form.kind !== "video") setForm((prev) => ({ ...prev, kind: "video" }));
      else if (!data?.video && form.kind === "announcement") setForm((prev) => ({ ...prev, kind: "link" }));
      if (!form.title.trim() && data?.preview?.title) {
        setForm((prev) => ({ ...prev, title: data.preview.title.slice(0, 200) }));
      }
    } catch (err) {
      setLinkPreview(null);
      setError(err.message);
    }
  };

  const onImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const data = await uploadNewsletterImage(file);
      setForm((prev) => ({ ...prev, image_url: data?.url || "" }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
    setLinkPreview(null);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!form.title.trim()) {
      setError(t("newsletterPostTitleRequired", "Add a title"));
      return;
    }
    setBusy(true);
    try {
      if (editingId) await updateNewsletterPost(editingId, form);
      else await createNewsletterPost(form);
      setNotice(
        editingId
          ? t("newsletterPostUpdated", "Post updated")
          : t("newsletterPostAdded", "Added to the next issue")
      );
      reset();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onEdit = (post) => {
    setEditingId(post.post_id);
    setForm(formFromPost(post));
    setLinkPreview(post.preview || null);
    setNotice("");
  };

  const onDelete = async (post) => {
    if (!window.confirm(t("newsletterPostDeleteConfirm", "Delete this post?"))) return;
    setBusy(true);
    try {
      await deleteNewsletterPost(post.post_id);
      if (editingId === post.post_id) reset();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const postWindow = (post) => {
    if (!post.publish_from && !post.expires_at) return "";
    return formatIssueRange(post.publish_from || post.expires_at, post.expires_at || "", i18n.language);
  };

  const renderList = (list, title) =>
    list.length ? (
      <>
        <h3 className="pingflo-subhead">{title}</h3>
        <ul className="pingflo-post-list">
          {list.map((post) => (
            <li key={post.post_id}>
              <span className="pingflo-chip">{kindLabel(post.kind)}</span>
              {post.pinned ? <span className="pingflo-chip is-pin">{t("newsletterPinned", "Pinned")}</span> : null}
              <span className="pingflo-name">{post.title}</span>
              {postWindow(post) ? <span className="pingflo-quiet">{postWindow(post)}</span> : null}
              {post.can_edit ? (
                <span className="pingflo-post-actions">
                  <button type="button" onClick={() => onEdit(post)} disabled={busy}>
                    {t("newsletterEdit", "Edit")}
                  </button>
                  <button type="button" onClick={() => onDelete(post)} disabled={busy}>
                    {t("newsletterDelete", "Delete")}
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </>
    ) : null;

  return (
    <section className="pingflo-setup">
      <h2>{editingId ? t("newsletterEditPost", "Edit post") : t("newsletterAddPost", "Add to the next issue")}</h2>
      <p className="pingflo-quiet">
        {t(
          "newsletterPostHelp",
          "Announcements, club news, or a link to an article or video. Pinned posts stay in every issue until their end date."
        )}
      </p>
      <form onSubmit={onSubmit}>
        <label>
          {t("newsletterPostKind", "Type")}
          <select value={form.kind} onChange={set("kind")}>
            {KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kindLabel(kind)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("newsletterPostTitle", "Title")}
          <input value={form.title} onChange={set("title")} maxLength={200} required />
        </label>
        <label>
          {t("newsletterPostBody", "Text")}
          <textarea value={form.body} onChange={set("body")} rows={4} maxLength={4000} />
        </label>
        <label>
          {t("newsletterPostUrl", "Link (article, video, registration page)")}
          <input
            type="url"
            inputMode="url"
            placeholder="https://"
            value={form.url}
            onChange={set("url")}
            onBlur={onUrlBlur}
          />
        </label>
        {linkPreview && (linkPreview.title || linkPreview.image) ? (
          <div className="pingflo-link-preview">
            {linkPreview.image ? <img src={linkPreview.image} alt="" /> : null}
            <div>
              <strong>{linkPreview.title}</strong>
              {linkPreview.site_name ? <span className="pingflo-quiet">{linkPreview.site_name}</span> : null}
            </div>
          </div>
        ) : null}
        <label>
          {t("newsletterPostImage", "Image (optional)")}
          <input type="file" accept="image/*" onChange={onImage} disabled={busy} />
        </label>
        {form.image_url ? (
          <div className="pingflo-link-preview">
            <img src={form.image_url} alt="" />
            <button type="button" onClick={() => setForm((prev) => ({ ...prev, image_url: "" }))}>
              {t("newsletterRemoveImage", "Remove image")}
            </button>
          </div>
        ) : null}
        <div className="pingflo-row">
          <label>
            {t("newsletterPostFrom", "Show from")}
            <input type="date" value={form.publish_from} onChange={set("publish_from")} />
          </label>
          <label>
            {t("newsletterPostUntil", "Until")}
            <input type="date" value={form.expires_at} onChange={set("expires_at")} />
          </label>
        </div>
        <label className="pingflo-check">
          <input type="checkbox" checked={form.pinned} onChange={set("pinned")} />
          {t("newsletterPinPost", "Pin to the top of every issue until the end date")}
        </label>
        {error ? <p className="pingflo-error">{error}</p> : null}
        {notice ? <p className="pingflo-banner">{notice}</p> : null}
        <div className="pingflo-actions">
          <button type="submit" className="is-send" disabled={busy}>
            {editingId ? t("newsletterSavePost", "Save post") : t("newsletterPublishPost", "Add post")}
          </button>
          {editingId ? (
            <button type="button" onClick={reset} disabled={busy}>
              {t("newsletterCancel", "Cancel")}
            </button>
          ) : null}
        </div>
      </form>
      {renderList(posts.upcoming, t("newsletterQueued", "In the next issue"))}
      {renderList(posts.past, t("newsletterPublishedPosts", "Already published"))}
    </section>
  );
}
